import { useState, useMemo, useEffect } from "react"
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import kanoPollingUnits from "../../data/kanoPollingUnits.json"
import locationIconImg from "../../assets/locationicon.png"
import "./PollingUnits.css"

// Fix for default markers in react-leaflet using CDN links (more reliable)
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

// Haversine distance helper
const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d.toFixed(2);
};

const PollingUnits = ({ language }) => {
  const lgOptions = useMemo(
    () => [...new Set(kanoPollingUnits.map((unit) => unit.lg.trim()))].sort((a, b) => a.localeCompare(b)),
    []
  )
  const [selectedLG, setSelectedLG] = useState(lgOptions[0] || "")
  const [searchTerm, setSearchTerm] = useState("")
  const [coords, setCoords] = useState([])
  const [originCoords, setOriginCoords] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const filteredUnits = useMemo(
    () => kanoPollingUnits.filter((unit) => unit.lg.trim() === selectedLG),
    [selectedLG]
  )

  // Create custom location icon
  const locationIcon = useMemo(() => {
    const icon = new L.Icon({
      iconUrl: locationIconImg,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    })
    console.log('Location icon created:', icon, 'URL:', locationIconImg)
    return icon
  }, [])

  useEffect(() => {
    const fetchCoords = async () => {
      setIsLoading(true)
      const newCoords = []

      // Fetch coordinates for ALL units in the selected LG
      const totalUnits = filteredUnits.length
      console.log(`Automatically fetching coords for all ${totalUnits} units in selected LG`)

      // For now, let's create markers with default coordinates to show the icons work
      // We'll use a base coordinate and offset each marker slightly
      const baseLat = 12.0022 // Kano latitude
      const baseLng = 8.5919  // Kano longitude

      for (let i = 0; i < filteredUnits.length; i++) {
        const unit = filteredUnits[i]

        // Create a simple offset pattern to spread markers
        const offsetLat = (Math.random() - 0.5) * 0.01 // Small random offset
        const offsetLng = (Math.random() - 0.5) * 0.01

        newCoords.push({
          id: unit.id,
          lat: baseLat + offsetLat,
          lng: baseLng + offsetLng,
          unit,
          distance: null
        })
      }

      if (originCoords) {
        newCoords.sort((a, b) => {
          const distA = getDistance(originCoords.lat, originCoords.lng, a.lat, a.lng)
          const distB = getDistance(originCoords.lat, originCoords.lng, b.lat, b.lng)
          return distA - distB
        })
      }

      setCoords(newCoords)
      setIsLoading(false)
    }

    if (filteredUnits.length > 0) {
      fetchCoords()
    } else {
      setCoords([])
    }
  }, [selectedLG, originCoords, filteredUnits])

  const mapCenter = useMemo(() => {
    if (originCoords) return [originCoords.lat, originCoords.lng]
    if (coords.length > 0) return [coords[0].lat, coords[0].lng]
    return [12.0022, 8.5919] // Default Kano
  }, [coords, originCoords])

  const content = {
    ha: {
      title: "Injin Zabe na Jihar Kano",
      subtitle: "Duba wuraren zabe da aka tattara daga bayanan jihar Kano kuma samun umarnin zuwa wurin.",
      lgaLabel: "Hukumar Gwamnati",
      searchLabel: "Sunan titi ko Wuri",
      searchPlaceholder: "Shigar da sunan titi ko wurin da yake kusa",
      findBtn: "Nemo Wuraren Zabe",
      allUnitsTitle: (lg) => `Duk Wuraren Zabe a ${lg} LGA`,
      getDirections: "Samun Hanyar",
      unitCode: "Lamba::",
      mapTitle: "Taswirar Kano",
      searching: "Ana bincike...",
      distance: "Nisa:",
    },
    en: {
      title: "Kano State Polling Units",
      subtitle: "Browse polling units extracted from the Kano state dataset and get directions to the selected unit.",
      lgaLabel: "Local Government Area",
      searchLabel: "Your Current Location (Street/Landmark)",
      searchPlaceholder: "Enter your street name (e.g. Gyadi-Gyadi)",
      findBtn: "Find Polling Units",
      allUnitsTitle: (lg) => `All Polling Units in ${lg} LGA`,
      getDirections: "Get Directions",
      unitCode: "Code::",
      mapTitle: "Kano Map View",
      searching: "Searching...",
      distance: "Distance:",
    },
  }

  const locale = content[language]

  const openDirections = (unit) => {
    const destination = encodeURIComponent(
      `${unit.location}, ${unit.lg}, Kano, Nigeria`
    )
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
      "_blank"
    )
  }

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setOriginCoords(null)
      return
    }

    setIsLoading(true)
    try {
      const query = `${searchTerm}, Kano, Nigeria`
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`)
      const data = await response.json()
      console.log(`Geocoding origin: ${searchTerm}`, data);
      if (data.length > 0) {
        setOriginCoords({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          name: searchTerm
        })
      } else {
        alert("Location not found. Please try a more specific landmark in Kano.")
      }
    } catch (error) {
      console.error("Error geocoding origin:", error)
    }
    setIsLoading(false)
  }

  return (
    <div className="polling-page">
      <header className="page-header polling-header fade-in">
        <div className="content-card-inner">
          <h1>{locale.title}</h1>
          <p>{locale.subtitle}</p>
        </div>
      </header>

      <section className="filter-section content-card fade-in" style={{ animationDelay: "0.1s" }}>
        <div className="filter-container">
          <div className="filter-group">
            <label htmlFor="lg-select">{locale.lgaLabel}</label>
            <select
              id="lg-select"
              value={selectedLG}
              onChange={(e) => setSelectedLG(e.target.value)}
            >
              {lgOptions.map((lg) => (
                <option key={lg} value={lg}>
                  {lg}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="search-input">{locale.searchLabel}</label>
            <input
              id="search-input"
              type="text"
              placeholder={locale.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button className="find-btn" onClick={handleSearch} disabled={isLoading}>
            {isLoading ? locale.searching : locale.findBtn}
          </button>
        </div>
      </section>

      <section className="map-section fade-in" style={{ animationDelay: "0.2s" }}>
        <div className="map-card content-card">
          <MapContainer center={mapCenter} zoom={originCoords ? 13 : 11} style={{ height: '450px', width: '100%' }} key={`${mapCenter[0]}-${mapCenter[1]}`}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {originCoords && (
              <Marker position={[originCoords.lat, originCoords.lng]} icon={locationIcon}>
                <Popup>
                  <strong>Your Location: {originCoords.name}</strong>
                </Popup>
              </Marker>
            )}
            {coords.map((coord) => (
              <Marker key={coord.id} position={[coord.lat, coord.lng]} icon={locationIcon}>
                <Popup>
                  <strong>{coord.unit.location}</strong><br />
                  Ward: {coord.unit.ward}<br />
                  Code: {coord.unit.code}
                  {coord.distance && (
                    <>
                      <br /><strong>Distance: {coord.distance} km</strong>
                    </>
                  )}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </section>

      <section className="units-results-section fade-in" style={{ animationDelay: "0.3s" }}>
        <h2 className="results-title">
          {originCoords ? `Polling Units near ${originCoords.name} in ${selectedLG}` : locale.allUnitsTitle(selectedLG)}
        </h2>
        <div className="unit-grid">
          {(originCoords && coords.length > 0 ? coords : filteredUnits).map((item, index) => {
            const unit = item.unit || item
            const distance = item.distance

            return (
              <div
                key={unit.id}
                className="unit-card-new content-card"
                style={{ animation: `fadeIn 0.5s ease-out forwards`, animationDelay: `${0.4 + (index % 10) * 0.05}s`, opacity: 0 }}
              >
                <div className="unit-info-new">
                  <div className="unit-header-row">
                    <h3 className="unit-name-new">{unit.location}</h3>
                    {distance && <span className="distance-badge">{distance} km</span>}
                  </div>
                  <p className="unit-code-new">{locale.unitCode} {unit.code}</p>
                  <p className="unit-address-new">{unit.ward}, {unit.lg}</p>
                </div>
                <button className="directions-btn-new" onClick={() => openDirections(unit)}>
                  {locale.getDirections}
                </button>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}

export default PollingUnits
